import { Fragment, useState } from "react";
import { Dialog, Listbox, Transition } from "@headlessui/react";
import {
  ChevronUpDownIcon,
  CheckIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  DocumentTextIcon,
  DocumentMagnifyingGlassIcon,
  ListBulletIcon,
  NewspaperIcon,
  ChatBubbleBottomCenterTextIcon,
  TableCellsIcon,
  ChartBarIcon,
  QuestionMarkCircleIcon,
  BookOpenIcon,
  QuestionMarkCircleIcon as FAQIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/20/solid";
import {
  trackPromptConfigOpen,
  trackPromptOptionSelect,
  trackPromptOptionDeselect,
  trackPromptStyleSelect,
  trackPromptLevelSelect,
} from "../utils/analytics";

export const styles = {
  neutral: "Clear and concise",
  academic: "Academic and formal",
  casual: "Casual and friendly",
  storytelling: "Narrative and engaging",
};

export const levels = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  expert: "Expert",
};

const contentTypes = {
  summary: { label: "Summary", icon: DocumentTextIcon },
  shortSummary: { label: "Short Summary", icon: DocumentMagnifyingGlassIcon },
  keyPoints: { label: "Key Points", icon: ListBulletIcon },
  article: { label: "Article", icon: NewspaperIcon },
  quotes: { label: "Quotes", icon: ChatBubbleBottomCenterTextIcon },
  table: { label: "Table", icon: TableCellsIcon },
  diagram: { label: "Diagram", icon: ChartBarIcon },
  questions: { label: "Questions", icon: QuestionMarkCircleIcon },
  flashcards: { label: "Flashcards", icon: BookOpenIcon },
  faq: { label: "FAQ", icon: FAQIcon },
  qa: { label: "Q&A", icon: ChatBubbleLeftRightIcon },
};

function ConfigSummary({ promptOptions, promptStyle, promptLevel }) {
  const activeTypes = Object.entries(contentTypes)
    .filter(([key]) => promptOptions[key])
    .map(([_, { label }]) => label);

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm p-4 w-[400px]">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
            <AdjustmentsHorizontalIcon className="w-4 h-4" />
            <span>GPT Configuration</span>
          </div>
          <div className="flex flex-wrap gap-2 items-center text-sm">
            <span className="font-medium text-gray-900 dark:text-white">
              {styles[promptStyle]}
            </span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-600 dark:text-gray-400">
              {levels[promptLevel]}
            </span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-600 dark:text-gray-400 truncate">
              {activeTypes.join(", ")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PromptConfigurator({
  promptOptions,
  setPromptOptions,
  promptStyle,
  setPromptStyle,
  promptLevel,
  setPromptLevel,
  isOpen,
  onClose,
}) {
  const handleOptionToggle = (key) => {
    const newValue = !promptOptions[key];
    const newOptions = {
      ...promptOptions,
      [key]: newValue,
    };
    setPromptOptions(newOptions);
    localStorage.setItem("promptOptions", JSON.stringify(newOptions));

    if (newValue) {
      trackPromptOptionSelect(key);
    } else {
      trackPromptOptionDeselect(key);
    }
  };

  const handleStyleChange = (value) => {
    setPromptStyle(value);
    localStorage.setItem("promptStyle", value);
    trackPromptStyleSelect(value);
  };

  const handleLevelChange = (value) => {
    setPromptLevel(value);
    localStorage.setItem("promptLevel", value);
    trackPromptLevelSelect(value);
  };

  const activeTypes = Object.entries(contentTypes)
    .filter(([key]) => promptOptions[key])
    .map(([_, { label }]) => label);

  return (
    <>
      <ConfigSummary
        promptOptions={promptOptions}
        promptStyle={promptStyle}
        promptLevel={promptLevel}
      />

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-visible rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold text-gray-900 dark:text-white"
                      >
                        GPT Configuration
                      </Dialog.Title>
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        These settings will be applied to all content generated
                        through ChatGPT. Choose your preferences carefully to
                        get the most suitable content format and style for your
                        needs.
                      </p>
                    </div>
                    <button
                      onClick={onClose}
                      className="rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>

                  <div className="space-y-8">
                    {/* Content Types */}
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
                        Content to Generate
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {Object.entries(contentTypes).map(
                          ([key, { label, icon: Icon }]) => (
                            <button
                              key={key}
                              onClick={() => handleOptionToggle(key)}
                              className={`
                              relative group overflow-hidden rounded-xl p-4 transition-all duration-300
                              ${
                                promptOptions[key]
                                  ? "bg-indigo-50 dark:bg-indigo-900/30 ring-2 ring-indigo-500/20 dark:ring-indigo-400/30"
                                  : "bg-gray-50 dark:bg-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-600/30"
                              }
                            `}
                            >
                              <div className="relative z-10 flex items-center gap-2">
                                <Icon
                                  className={`w-5 h-5 ${
                                    promptOptions[key]
                                      ? "text-indigo-600 dark:text-indigo-400"
                                      : "text-gray-500 dark:text-gray-400"
                                  }`}
                                />
                                <span
                                  className={`
                                text-sm font-medium
                                ${
                                  promptOptions[key]
                                    ? "text-indigo-900 dark:text-indigo-200"
                                    : "text-gray-700 dark:text-gray-300"
                                }
                              `}
                                >
                                  {label}
                                </span>
                              </div>
                              <div
                                className={`
                              absolute inset-0 transition-opacity duration-300
                              ${
                                promptOptions[key] ? "opacity-100" : "opacity-0"
                              }
                              bg-gradient-to-br from-indigo-50/50 to-indigo-100/50
                              dark:from-indigo-900/20 dark:to-indigo-800/20
                            `}
                              />
                            </button>
                          )
                        )}
                      </div>
                    </div>

                    {/* Writing Style and Target Level */}
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Writing Style Dropdown */}
                      <div>
                        <Listbox
                          value={promptStyle}
                          onChange={handleStyleChange}
                        >
                          <div className="relative">
                            <Listbox.Label className="block text-base font-semibold text-gray-900 dark:text-white mb-2">
                              Writing Style
                            </Listbox.Label>
                            <Listbox.Button className="relative w-full cursor-pointer rounded-xl bg-white dark:bg-gray-700 py-4 pl-6 pr-10 text-left shadow-sm ring-1 ring-gray-200 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400">
                              <span className="block truncate text-gray-900 dark:text-white">
                                {styles[promptStyle]}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                                <ChevronUpDownIcon
                                  className="h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white dark:bg-gray-700 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {Object.entries(styles).map(([key, label]) => (
                                  <Listbox.Option
                                    key={key}
                                    value={key}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-3 pl-10 pr-4 ${
                                        active
                                          ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-900 dark:text-indigo-200"
                                          : "text-gray-900 dark:text-white"
                                      }`
                                    }
                                  >
                                    {({ selected }) => (
                                      <>
                                        <span
                                          className={`block truncate ${
                                            selected
                                              ? "font-medium"
                                              : "font-normal"
                                          }`}
                                        >
                                          {label}
                                        </span>
                                        {selected ? (
                                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600 dark:text-indigo-400">
                                            <CheckIcon
                                              className="h-5 w-5"
                                              aria-hidden="true"
                                            />
                                          </span>
                                        ) : null}
                                      </>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                      </div>

                      {/* Target Level Dropdown */}
                      <div>
                        <Listbox
                          value={promptLevel}
                          onChange={handleLevelChange}
                        >
                          <div className="relative">
                            <Listbox.Label className="block text-base font-semibold text-gray-900 dark:text-white mb-2">
                              Target Level
                            </Listbox.Label>
                            <Listbox.Button className="relative w-full cursor-pointer rounded-xl bg-white dark:bg-gray-700 py-4 pl-6 pr-10 text-left shadow-sm ring-1 ring-gray-200 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400">
                              <span className="block truncate text-gray-900 dark:text-white">
                                {levels[promptLevel]}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                                <ChevronUpDownIcon
                                  className="h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white dark:bg-gray-700 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {Object.entries(levels).map(([key, label]) => (
                                  <Listbox.Option
                                    key={key}
                                    value={key}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-3 pl-10 pr-4 ${
                                        active
                                          ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-900 dark:text-indigo-200"
                                          : "text-gray-900 dark:text-white"
                                      }`
                                    }
                                  >
                                    {({ selected }) => (
                                      <>
                                        <span
                                          className={`block truncate ${
                                            selected
                                              ? "font-medium"
                                              : "font-normal"
                                          }`}
                                        >
                                          {label}
                                        </span>
                                        {selected ? (
                                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600 dark:text-indigo-400">
                                            <CheckIcon
                                              className="h-5 w-5"
                                              aria-hidden="true"
                                            />
                                          </span>
                                        ) : null}
                                      </>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                      </div>
                    </div>
                  </div>

                  {/* Confirm Button */}
                  <div className="mt-8 flex justify-end">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                    >
                      Confirm Settings
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

export default PromptConfigurator;
